/**
 * =========================================================
 * DACI Frontend - Componente de Pestañas Personalizado
 * =========================================================
 * 
 * Componente wrapper que encapsula el componente Tabs de MUI
 * con soporte automático para tema oscuro y estilos consistentes
 * en toda la aplicación.
 * 
 * Características:
 * - Soporte automático para tema oscuro/claro
 * - Estilos consistentes en toda la aplicación
 * - Responsive design integrado
 * - Fácil mantenimiento centralizado
 * - Compatible con todas las props de MUI Tabs
 */

import React from "react";
import { Tabs, Tab, Box } from "@mui/material";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";

/**
 * Componente de pestañas personalizado con soporte para tema oscuro
 * 
 * @param {Object} props - Props del componente
 * @param {number} props.value - Valor de la pestaña activa
 * @param {function} props.onChange - Función callback cuando cambia la pestaña
 * @param {Array} props.tabs - Array de objetos con las pestañas {value, label, wrapped}
 * @param {Object} props.containerSx - Estilos adicionales para el contenedor (opcional)
 * @param {Object} props.sx - Estilos adicionales para las pestañas (opcional)
 * @param {...any} rest - Resto de props que se pasan al componente Tabs de MUI
 * @returns {JSX.Element} Componente de pestañas con tema aplicado
 */
const DACITabs = ({ 
  value, 
  onChange, 
  tabs = [], 
  containerSx = {}, 
  sx = {}, 
  ...rest 
}) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Estilos del contenedor por defecto
  const defaultContainerStyles = {
    width: { xs: "92%", sm: "35%" },
    mt: 3,
    position: "relative",
    left: 15,
    right: 1,
  };

  // Estilos base para las pestañas con tema oscuro/claro
  const baseTabsStyles = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    "& .MuiTab-root": {
      textTransform: "none",
      borderBottom: "none",
      borderRadius: "4px 4px 0 0",
      marginRight: { sm: "4px" },
      marginBottom: { xs: "4px", sm: "0" },
      backgroundColor: darkMode ? "#202940" : "#f0f0f0",
      color: darkMode ? "#ffffff" : "#000000",
      padding: "5px 4px",
      flex: 1,
      "&:hover": {
        backgroundColor: darkMode ? "#616161" : "#e0e0e0",
      },
      "&.Mui-selected": {
        border: "2px solid #3e98ef",
        backgroundColor: darkMode ? "#202940" : "#ffffff",
        color: darkMode ? "#ffffff" : "#000000",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        borderBottom: "none",
      },
      minWidth: { sm: 200 },
      "@media (max-width: 600px)": {
        minWidth: "100%",
      },
    },
    "& .MuiTabs-scrollButtons": {
      "&.Mui-disabled": { opacity: 0.3 },
    },
    ...sx, // Permitir estilos adicionales
  };

  return (
    <>
      <Box sx={{ ...defaultContainerStyles, ...containerSx }}>
        <Tabs
          value={value}
          onChange={onChange}
          aria-label="pestañas personalizadas"
          sx={baseTabsStyles}
          {...rest}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              value={tab.value}
              label={tab.label}
              wrapped={tab.wrapped || false}
            />
          ))}
        </Tabs>
      </Box>
      
      {/* Línea decorativa debajo de las pestañas */}
      <Box
        sx={{
          position: "relative",
          width: { xs: "89.5%", sm: "98%" },
          bottom: 0,
          left: 19,
          right: 1,
          height: "2px",
          backgroundColor: "#3e98ef",
          top: { xs: "-8px", sm: "-4px" },
        }}
      />
    </>
  );
};

// PropTypes
DACITabs.propTypes = {
  /**
   * Valor de la pestaña activa
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  
  /**
   * Función callback cuando cambia la pestaña
   */
  onChange: PropTypes.func.isRequired,
  
  /**
   * Array de objetos con las pestañas
   * Cada objeto debe tener: {value, label, wrapped (opcional)}
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      wrapped: PropTypes.bool,
    })
  ).isRequired,
  
  /**
   * Estilos adicionales para el contenedor
   */
  containerSx: PropTypes.object,
  
  /**
   * Estilos adicionales para las pestañas
   */
  sx: PropTypes.object,
};

// Valores por defecto
DACITabs.defaultProps = {
  tabs: [],
  containerSx: {},
  sx: {},
};

export default DACITabs;